import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LogoBicolor } from './LogoBicolor';
import { Button } from './ui/button';
import {
  LayoutDashboard, Users, CreditCard, ShieldCheck, Server,
  LogOut, Moon, Sun, MoreHorizontal, X, KeyRound, ArrowLeft
} from 'lucide-react';

const ADMIN_NAV = [
  { name: 'Visão Geral', href: '/admin/dashboard', icon: LayoutDashboard, testId: 'admin-nav-overview' },
  { name: 'Usuários', href: '/admin/users', icon: Users, testId: 'admin-nav-users' },
  { name: 'Assinaturas', href: '/admin/billing', icon: CreditCard, testId: 'admin-nav-billing' },
  { name: 'Administradores', href: '/admin/members', icon: ShieldCheck, testId: 'admin-nav-members' },
  { name: 'Sistema', href: '/admin/system', icon: Server, testId: 'admin-nav-system' },
];

export const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin-akali-2026'); };

  const accent = isDark
    ? 'border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-red-500/10'
    : 'border-amber-200 bg-gradient-to-r from-amber-50 to-red-50';

  return (
    <div className={`min-h-screen relative ${isDark ? 'bg-[#0F1117]' : 'bg-slate-50'}`} data-testid="admin-layout">
      {/* Mobile Header */}
      <header
        className={`md:hidden sticky top-0 z-30 border-b backdrop-blur-md ${
          isDark ? 'bg-[#13151F]/90 border-[#2A2E42]' : 'bg-white/90 border-slate-200'
        }`}
        data-testid="admin-mobile-header"
      >
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-500" />
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Admin</span>
          </div>
          <div className="flex items-center gap-1">
            <Button onClick={toggleTheme} variant="ghost" size="icon" className="h-10 w-10" data-testid="admin-mobile-theme">
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="icon" className="h-10 w-10" data-testid="admin-mobile-logout">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar Desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col z-20">
        <div className={`flex flex-col flex-grow ${isDark ? 'bg-[#13151F]/95 border-[#2A2E42]' : 'bg-white border-slate-200'} border-r`}>
          <div className={`flex items-center justify-between px-6 py-6 border-b ${isDark ? 'border-[#2A2E42]' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <LogoBicolor size={18} symbolSize={28} />
              <div>
                <p className={`text-xs font-semibold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>ADMIN</p>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Painel</p>
              </div>
            </div>
            <Button onClick={toggleTheme} variant="ghost" size="icon" data-testid="admin-theme-toggle">
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>

          <div className={`mx-4 my-4 rounded-lg p-3 border ${accent}`} data-testid="admin-context-card">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-500" />
              <p className={`text-xs font-medium ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
                {user?.is_founder ? 'Founder' : 'Admin convidado'}
              </p>
            </div>
            <p className={`text-xs mt-1 truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{user?.email}</p>
          </div>

          <nav className="flex-1 px-4 pb-6 space-y-1">
            {ADMIN_NAV.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? isDark
                        ? 'bg-amber-500/15 text-amber-300 border-l-[3px] border-amber-500 font-medium'
                        : 'bg-amber-50 text-amber-700 font-medium'
                      : isDark
                        ? 'text-[#C8CEDE] hover:bg-[#22263A] hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={item.testId}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className={`px-4 py-4 border-t space-y-1 ${isDark ? 'border-[#2A2E42]' : 'border-slate-200'}`}>
            <Link
              to="/admin/change-password"
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${
                isDark ? 'text-[#C8CEDE] hover:bg-[#22263A]' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="admin-change-password-link"
            >
              <KeyRound className="h-4 w-4" /> Alterar senha
            </Link>
            <Link
              to="/dashboard"
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${
                isDark ? 'text-[#C8CEDE] hover:bg-[#22263A]' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="admin-go-to-app-link"
            >
              <ArrowLeft className="h-4 w-4" /> Ir para o app
            </Link>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full justify-start ${isDark ? 'text-[#C8CEDE] hover:bg-[#22263A]' : 'text-slate-600'}`}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-3" /> Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:pl-64 relative">
        <main className="py-6 px-4 sm:px-6 lg:px-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-30 border-t backdrop-blur-md ${
          isDark ? 'bg-[#13151F]/95 border-[#2A2E42]' : 'bg-white/95 border-slate-200'
        }`}
        data-testid="admin-mobile-bottom-nav"
      >
        <ul className="grid grid-cols-5">
          {ADMIN_NAV.slice(0, 4).map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium ${
                    isActive ? 'text-amber-500' : isDark ? 'text-[#C8CEDE]' : 'text-slate-600'
                  }`}
                  data-testid={`mobile-${item.testId}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="truncate px-1">{item.name}</span>
                </Link>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={`w-full flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium ${
                isDark ? 'text-[#C8CEDE]' : 'text-slate-600'
              }`}
              data-testid="admin-mobile-more-btn"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>Mais</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Mobile drawer */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMoreOpen(false)} />
          <div className={`absolute bottom-0 left-0 right-0 rounded-t-2xl pt-3 pb-6 px-4 ${isDark ? 'bg-[#13151F] text-white' : 'bg-white text-slate-900'}`}>
            <div className="flex justify-between items-center mb-4 px-2">
              <p className="text-sm font-semibold">Mais</p>
              <button onClick={() => setMoreOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-[#22263A]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-1">
              <Link to="/admin/system" onClick={() => setMoreOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'}`}>
                <Server className="h-5 w-5 text-amber-500" /> Sistema
              </Link>
              <Link to="/admin/change-password" onClick={() => setMoreOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'}`}>
                <KeyRound className="h-5 w-5 text-amber-500" /> Alterar senha
              </Link>
              <Link to="/dashboard" onClick={() => setMoreOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'}`}>
                <ArrowLeft className="h-5 w-5" /> Ir para o app
              </Link>
              <button onClick={() => { setMoreOpen(false); handleLogout(); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${isDark ? 'hover:bg-[#22263A]' : 'hover:bg-slate-100'}`}>
                <LogOut className="h-5 w-5 text-red-500" /> Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
